'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const subjects = [
    { name: 'Mathematics', progress: 72, grade: 'A', sessions: 14 },
    { name: 'English Language', progress: 88, grade: 'A+', sessions: 11 },
    { name: 'Physics', progress: 55, grade: 'B+', sessions: 8 },
    { name: 'Chemistry', progress: 41, grade: 'B', sessions: 6 },
];

const milestones = [
    { label: 'First program completed', date: 'Jan 12', done: true },
    { label: '10 live sessions attended', date: 'Feb 3', done: true },
    { label: 'First assessment passed', date: 'Feb 18', done: true },
    { label: 'Top 10% in Mathematics', date: 'Mar 5', done: false },
];

export default function LearnProgress() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="progress" ref={sectionRef} className="py-28 px-6 md:px-10 relative overflow-hidden" style={{ background: '#fff' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 80%, rgba(55,87,255,0.04) 0%, transparent 60%)' }} />

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <div
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                        style={{ background: 'rgba(55,87,255,0.08)', color: BRAND, border: '1px solid rgba(55,87,255,0.15)', letterSpacing: '0.15em' }}
                    >
                        Progress Tracking
                    </div>
                    <div className="grid lg:grid-cols-2 gap-8 items-end">
                        <h2
                            className="font-display font-800"
                            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            See exactly
                            <br />
                            <span style={{ color: BRAND }}>where you stand.</span>
                        </h2>
                        <p className="text-base leading-relaxed" style={{ color: '#666', maxWidth: '420px' }}>
                            Wisly Learn gives students and parents a clear, real-time view of academic progress — across every subject, session, and assessment.
                        </p>
                    </div>
                </div>

                {/* Bento grid */}
                <div className={`grid lg:grid-cols-3 gap-5 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {/* Subject progress — spans 2 cols */}
                    <div className="lg:col-span-2 rounded-3xl p-7" style={{ background: '#FAFAFA', border: '1px solid rgba(55,87,255,0.08)' }}>
                        <p className="text-sm font-800 mb-6" style={{ color: '#0D0D0D' }}>Subject Progress</p>
                        <div className="flex flex-col gap-5">
                            {subjects.map((sub) => (
                                <div key={sub.name}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <p className="text-sm font-700" style={{ color: '#0D0D0D' }}>{sub.name}</p>
                                            <span className="text-xs font-700 px-2 py-0.5 rounded-md" style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}>{sub.grade}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-600" style={{ color: '#888' }}>{sub.sessions} sessions</span>
                                            <span className="text-sm font-800" style={{ color: '#0D0D0D' }}>{sub.progress}%</span>
                                        </div>
                                    </div>
                                    <div className="w-full h-2 rounded-full" style={{ background: 'rgba(55,87,255,0.08)' }}>
                                        <div
                                            className="h-2 rounded-full transition-all duration-1000"
                                            style={{ width: visible ? `${sub.progress}%` : '0%', background: BRAND }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Milestones */}
                    <div className="rounded-3xl p-7" style={{ background: '#FAFAFA', border: '1px solid rgba(55,87,255,0.08)' }}>
                        <p className="text-sm font-800 mb-6" style={{ color: '#0D0D0D' }}>Milestones</p>
                        <div className="flex flex-col gap-4">
                            {milestones.map((m) => (
                                <div key={m.label} className="flex items-start gap-3">
                                    <div
                                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                                        style={{ background: m.done ? BRAND : 'rgba(55,87,255,0.1)', border: m.done ? 'none' : `1.5px dashed rgba(55,87,255,0.3)` }}
                                    >
                                        {m.done ? (
                                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        ) : (
                                            <div className="w-2 h-2 rounded-full" style={{ background: 'rgba(55,87,255,0.3)' }} />
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-sm font-600" style={{ color: m.done ? '#0D0D0D' : '#888' }}>{m.label}</p>
                                        {m.done && <p className="text-xs font-500 mt-0.5" style={{ color: '#AAA' }}>{m.date}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Stats row */}
                    {[
                        { label: 'Overall Average', value: '87%', sub: 'Across all subjects' },
                        { label: 'Sessions Attended', value: '39', sub: 'This semester' },
                        { label: 'Assessments Passed', value: '12/14', sub: '86% pass rate' },
                    ].map((stat, i) => (
                        <div
                            key={stat.label}
                            className="rounded-2xl p-6 transition-all duration-300 cursor-default"
                            style={{ background: i === 0 ? BRAND : '#FAFAFA', border: i === 0 ? 'none' : '1px solid rgba(55,87,255,0.08)' }}
                            onMouseEnter={e => { if (i !== 0) { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.25)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; } }}
                            onMouseLeave={e => { if (i !== 0) { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.08)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; } }}
                        >
                            <p className="text-3xl font-800 mb-1" style={{ color: i === 0 ? 'white' : BRAND }}>{stat.value}</p>
                            <p className="text-sm font-700 mb-0.5" style={{ color: i === 0 ? 'rgba(255,255,255,0.9)' : '#0D0D0D' }}>{stat.label}</p>
                            <p className="text-xs font-500" style={{ color: i === 0 ? 'rgba(255,255,255,0.6)' : '#888' }}>{stat.sub}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
